import { isNavigationHotspotRef } from "./worldJson";

/** App-level handlers for Unity → React bridge events (decoupled from UnityMessagePanel). */

let hotspotClickHandler = null;
let navigationHotspotHandler = null;
let buildingEnteredHandler = null;
let roomEnteredHandler = null;
let buildingExitedHandler = null;
let doorClickedHandler = null;
let bookClickedHandler = null;
let bookAddedHandler = null;
let bookAddFailedHandler = null;
let booksReorderedHandler = null;
let hotspotClosedHandler = null;
let worldReadyHandler = null;
let sessionRestoredHandler = null;
let sessionStateHandler = null;
let layoutCorrectedHandler = null;
let patchFailedHandler = null;
let islandRemovedHandler = null;
let buildingColorsAssignedHandler = null;
let unityInBuildingSession = false;
let pendingWorldSyncHandler = null;
let unityWorldBuilt = false;
const islandsRemovedByUnity = new Set();

export function isUnityWorldBuilt() {
  return unityWorldBuilt;
}

export function markUnityWorldLoading() {
  unityWorldBuilt = false;
  islandsRemovedByUnity.clear();
}

export function takeIslandRemovedByUnity(islandId) {
  return islandsRemovedByUnity.delete(islandId);
}

export function isUnityInBuildingSession() {
  return unityInBuildingSession;
}

export function setUnityInBuildingSession(active) {
  unityInBuildingSession = Boolean(active);
  if (!unityInBuildingSession && pendingWorldSyncHandler) {
    pendingWorldSyncHandler();
  }
}

export function setUnityPendingWorldSyncHandler(handler) {
  pendingWorldSyncHandler = typeof handler === "function" ? handler : null;
}

export function requestUnityWorldSync(isClearWorld = false) {
  pendingWorldSyncHandler?.(isClearWorld);
}

export function setUnityHotspotClickHandler(handler) {
  hotspotClickHandler = typeof handler === "function" ? handler : null;
}

export function setUnityNavigationHotspotHandler(handler) {
  navigationHotspotHandler = typeof handler === "function" ? handler : null;
}

export function setUnityBuildingEnteredHandler(handler) {
  buildingEnteredHandler = typeof handler === "function" ? handler : null;
}

export function setUnityRoomEnteredHandler(handler) {
  roomEnteredHandler = typeof handler === "function" ? handler : null;
}

export function setUnityBuildingExitedHandler(handler) {
  buildingExitedHandler = typeof handler === "function" ? handler : null;
}

export function setUnityDoorClickedHandler(handler) {
  doorClickedHandler = typeof handler === "function" ? handler : null;
}

export function setUnityBookClickedHandler(handler) {
  bookClickedHandler = typeof handler === "function" ? handler : null;
}

export function setUnityBookAddedHandler(handler) {
  bookAddedHandler = typeof handler === "function" ? handler : null;
}

export function setUnityBookAddFailedHandler(handler) {
  bookAddFailedHandler = typeof handler === "function" ? handler : null;
}

export function setUnityBooksReorderedHandler(handler) {
  booksReorderedHandler = typeof handler === "function" ? handler : null;
}

export function setUnityHotspotClosedHandler(handler) {
  hotspotClosedHandler = typeof handler === "function" ? handler : null;
}

export function setUnityWorldReadyHandler(handler) {
  worldReadyHandler = typeof handler === "function" ? handler : null;
}

export function setUnitySessionRestoredHandler(handler) {
  sessionRestoredHandler = typeof handler === "function" ? handler : null;
}

export function setUnitySessionStateHandler(handler) {
  sessionStateHandler = typeof handler === "function" ? handler : null;
}

export function setUnityLayoutCorrectedHandler(handler) {
  layoutCorrectedHandler = typeof handler === "function" ? handler : null;
}

export function setUnityPatchFailedHandler(handler) {
  patchFailedHandler = typeof handler === "function" ? handler : null;
}

export function setUnityIslandRemovedHandler(handler) {
  islandRemovedHandler = typeof handler === "function" ? handler : null;
}

export function setUnityBuildingColorsAssignedHandler(handler) {
  buildingColorsAssignedHandler = typeof handler === "function" ? handler : null;
}

function parseUnityPayload(payload) {
  if (payload == null) return null;
  if (typeof payload === "string") {
    try {
      return JSON.parse(payload);
    } catch {
      return payload;
    }
  }
  return payload;
}

/** True when a Unity id/name string is missing or was coerced from an object. */
export function isUselessUnityRef(value) {
  if (value == null) return true;
  const text = String(value).trim();
  return (
    !text ||
    text === "[object Object]" ||
    text === "undefined" ||
    text === "null"
  );
}

/** Peel react-unity-webgl / proxy wrappers until we reach the inner payload. */
export function unwrapUnityPayload(payload) {
  let current = payload;
  for (let depth = 0; depth < 5; depth += 1) {
    if (typeof current === "string") {
      const parsed = parseUnityPayload(current);
      if (parsed === current) break;
      current = parsed;
      continue;
    }
    if (!current || typeof current !== "object" || Array.isArray(current)) break;

    if (current.jsonData != null) {
      current = current.jsonData;
      continue;
    }
    if (current.event && current.payload != null) {
      current = current.payload;
      continue;
    }
    if (current.eventName && (current.payload != null || current.jsonData != null)) {
      current = current.payload ?? current.jsonData;
      continue;
    }
    if (current.detail != null) {
      current = current.detail;
      continue;
    }
    if (current.data != null) {
      current = current.data;
      continue;
    }
    break;
  }
  return current;
}

function collectPayloadStrings(value, out = new Set(), depth = 0) {
  if (depth > 5 || value == null) return out;
  if (typeof value === "string") {
    if (!isUselessUnityRef(value)) out.add(value.trim());
    return out;
  }
  if (typeof value !== "object") return out;

  for (const entry of Object.values(value)) {
    if (typeof entry === "string") {
      if (!isUselessUnityRef(entry)) out.add(entry.trim());
    } else if (entry && typeof entry === "object") {
      collectPayloadStrings(entry, out, depth + 1);
    }
  }
  return out;
}

const HOTSPOT_ID_KEYS = [
  "id",
  "hotspotID",
  "hotspotId",
  "surfaceItemId",
  "surface",
  "surfaceId",
  "surfaceID",
  "name",
  "hotspotName",
  "buildingName",
  "buildingID",
  "buildingId",
  "label",
  "title",
  "gameObjectName",
  "objectName",
  "targetName",
  "hitName",
];

/** Normalize Unity HotspotClicked payload to a surface_item id string. */
export function normalizeHotspotPayload(payload) {
  const parsed = unwrapUnityPayload(parseUnityPayload(payload));
  if (parsed == null) return null;
  if (typeof parsed === "string") {
    const text = parsed.trim();
    return isUselessUnityRef(text) ? null : text;
  }
  if (typeof parsed === "object") {
    for (const key of HOTSPOT_ID_KEYS) {
      const value = parsed[key];
      if (!isUselessUnityRef(value)) return String(value).trim();
    }
    for (const candidate of collectPayloadStrings(parsed)) {
      return candidate;
    }
    return null;
  }
  const text = String(parsed).trim();
  return isUselessUnityRef(text) ? null : text;
}

function readBuildingId(payload) {
  const parsed = parseUnityPayload(payload);
  if (parsed == null) return null;
  if (typeof parsed === "string") return parsed.trim() || null;
  return parsed.buildingID ?? parsed.buildingId ?? null;
}

function readRoomEntered(payload) {
  const parsed = parseUnityPayload(payload);
  if (!parsed || typeof parsed !== "object") return null;
  const buildingID = parsed.buildingID ?? parsed.buildingId ?? null;
  const roomID = parsed.roomID ?? parsed.roomId ?? null;
  if (!roomID) return null;
  return { buildingID, roomID, raw: parsed };
}

export function dispatchUnityBridgeEvent(event, payload) {
  const envelope = parseUnityPayload(payload);
  if (
    envelope &&
    typeof envelope === "object" &&
    typeof envelope.event === "string" &&
    envelope.event !== event
  ) {
    dispatchUnityBridgeEvent(envelope.event, envelope.payload ?? null);
    return;
  }

  switch (event) {
    case "BridgeReady":
      requestUnityWorldSync();
      return;
    case "HotspotClicked": {
      const unwrapped = unwrapUnityPayload(payload);
      const hotspotId = normalizeHotspotPayload(unwrapped);
      const hasPayloadObject = unwrapped != null && typeof unwrapped === "object";

      if ((!hotspotId && !hasPayloadObject) || !hotspotClickHandler) {
        if (hotspotId && isNavigationHotspotRef(hotspotId) && navigationHotspotHandler) {
          try {
            navigationHotspotHandler(hotspotId, unwrapped);
          } catch (e) {
            console.error("[Unity → React] navigation hotspot handler error", e);
          }
        }
        return;
      }

      const isNav = hotspotId ? isNavigationHotspotRef(hotspotId) : false;

      try {
        hotspotClickHandler(hotspotId, unwrapped);
      } catch (e) {
        console.error("[Unity → React] HotspotClicked handler error", e);
      }

      if (isNav && navigationHotspotHandler) {
        try {
          navigationHotspotHandler(hotspotId, unwrapped);
        } catch (e) {
          console.error("[Unity → React] navigation hotspot handler error", e);
        }
      }
      return;
    }
    case "DoorClicked":
      if (doorClickedHandler) {
        try {
          doorClickedHandler(parseUnityPayload(payload));
        } catch (e) {
          console.error("[Unity → React] DoorClicked handler error", e);
        }
        return;
      }
      if (navigationHotspotHandler) {
        try {
          navigationHotspotHandler("door", payload);
        } catch (e) {
          console.error("[Unity → React] DoorClicked handler error", e);
        }
        return;
      }
      if (!buildingExitedHandler) return;
      try {
        buildingExitedHandler(parseUnityPayload(payload));
      } catch (e) {
        console.error("[Unity → React] DoorClicked handler error", e);
      }
      return;
    case "BuildingEntered": {
      const buildingID = readBuildingId(payload);
      if (!buildingID || !buildingEnteredHandler) return;
      try {
        buildingEnteredHandler(buildingID, parseUnityPayload(payload));
      } catch (e) {
        console.error("[Unity → React] BuildingEntered handler error", e);
      }
      return;
    }
    case "RoomEntered": {
      const data = readRoomEntered(payload);
      if (!data || !roomEnteredHandler) return;
      try {
        roomEnteredHandler(data);
      } catch (e) {
        console.error("[Unity → React] RoomEntered handler error", e);
      }
      return;
    }
    // ── Bookshelf ────────────────────────────────────────────
    case "BookClicked": {
      const data = parseUnityPayload(payload);
      const bookID = data?.bookID ?? data?.bookId;
      if (!bookID || !bookClickedHandler) return;
      try {
        bookClickedHandler(String(bookID), data);
      } catch (e) {
        console.error("[Unity → React] BookClicked handler error", e);
      }
      return;
    }
    case "BookAdded": {
      const data = parseUnityPayload(payload);
      const bookID = data?.bookID ?? data?.bookId;
      if (!bookID || !bookAddedHandler) return;
      try {
        bookAddedHandler(String(bookID), Number(data?.slot), data);
      } catch (e) {
        console.error("[Unity → React] BookAdded handler error", e);
      }
      return;
    }
    case "BookAddFailed": {
      const data = parseUnityPayload(payload);
      const bookID = data?.bookID ?? data?.bookId;
      if (!bookID || !bookAddFailedHandler) return;
      try {
        bookAddFailedHandler(String(bookID), data?.reason || "full", data);
      } catch (e) {
        console.error("[Unity → React] BookAddFailed handler error", e);
      }
      return;
    }
    case "BooksReordered": {
      const data = parseUnityPayload(payload);
      const books = Array.isArray(data?.books) ? data.books : null;
      if (!books || !booksReorderedHandler) return;
      try {
        booksReorderedHandler(books, data);
      } catch (e) {
        console.error("[Unity → React] BooksReordered handler error", e);
      }
      return;
    }
    case "HotspotClosed":
      if (!hotspotClosedHandler) return;
      try {
        hotspotClosedHandler();
      } catch (e) {
        console.error("[Unity → React] HotspotClosed handler error", e);
      }
      return;
    // ── Session restore (KAN-189) ────────────────────────────
    case "WorldReady": {
      const data = parseUnityPayload(payload);
      unityWorldBuilt = true;
      if (!worldReadyHandler) return;
      try {
        worldReadyHandler({ isNewUser: data?.isNewUser === true });
      } catch (e) {
        console.error("[Unity → React] WorldReady handler error", e);
      }
      return;
    }
    case "SessionRestored": {
      const data = parseUnityPayload(payload);
      if (!sessionRestoredHandler) return;
      try {
        // SessionRestored uses roomID / buildingID (SessionState uses roomId / buildingId).
        sessionRestoredHandler({
          mode: data?.mode ?? null,
          roomID: data?.roomID ?? null,
          buildingID: data?.buildingID ?? null,
          hotspotID: data?.hotspotID ?? null,
          fallback: data?.fallback === true,
        });
      } catch (e) {
        console.error("[Unity → React] SessionRestored handler error", e);
      }
      return;
    }
    case "SessionState": {
      const data = parseUnityPayload(payload);
      if (!data || typeof data !== "object" || !sessionStateHandler) return;
      try {
        sessionStateHandler(data);
      } catch (e) {
        console.error("[Unity → React] SessionState handler error", e);
      }
      return;
    }
    case "LayoutCorrected": {
      const data = parseUnityPayload(payload);
      if (!data || typeof data !== "object" || !layoutCorrectedHandler) return;
      try {
        layoutCorrectedHandler({
          districts: Array.isArray(data.districts) ? data.districts : [],
          islands: Array.isArray(data.islands) ? data.islands : [],
        });
      } catch (e) {
        console.error("[Unity → React] LayoutCorrected handler error", e);
      }
      return;
    }
    case "BuildingColorsAssigned": {
      const data = parseUnityPayload(payload);
      if (!Array.isArray(data?.buildings) || !buildingColorsAssignedHandler) return;
      try {
        buildingColorsAssignedHandler(data.buildings);
      } catch (e) {
        console.error("[Unity → React] BuildingColorsAssigned handler error", e);
      }
      return;
    }
    case "PatchFailed": {
      const data = parseUnityPayload(payload);
      console.warn("[Unity → React] PatchFailed", data);
      if (!data || typeof data !== "object" || !patchFailedHandler) return;
      try {
        patchFailedHandler(data);
      } catch (e) {
        console.error("[Unity → React] PatchFailed handler error", e);
      }
      return;
    }
    case "IslandRemoved": {
      const data = parseUnityPayload(payload);
      const islandID = data?.islandID ?? data?.islandId;
      if (!islandID) return;
      islandsRemovedByUnity.add(String(islandID));
      if (!islandRemovedHandler) return;
      try {
        islandRemovedHandler(String(islandID));
      } catch (e) {
        console.error("[Unity → React] IslandRemoved handler error", e);
      }
      return;
    }
    case "BuildingExited":
      if (!buildingExitedHandler) return;
      try {
        buildingExitedHandler(parseUnityPayload(payload));
      } catch (e) {
        console.error("[Unity → React] BuildingExited handler error", e);
      }
      break;
    default:
      break;
  }
}
